/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Preferences } from '@capacitor/preferences';
import { TokenAuthMutation, UserRegisterMutation, UserType } from '../generated/graphql';

interface ActionType {
  type: 'LOGIN' | 'LOGOUT' | 'REGISTER' | 'SET_USER';
  payload?: StateType;
  // TokenAuthMutation & UserRegisterMutation;
}

interface StateType {
  user?: Partial<UserType> | null;
  registered: boolean;
}

export interface AuthContextType extends StateType {
  login: (tokenData: TokenAuthMutation) => void;
  register: (userData: UserRegisterMutation) => void;
  logout: () => void;
  setUser: (user: Partial<UserType>) => void;
  isFetchingFromStorage: boolean;
}

// eslint-disable-next-line consistent-return
const getInitialStateFromLocalStorage = async (dispatch: React.Dispatch<ActionType>) => {
  const { value } = await Preferences.get({ key: 'user' });
  if (value) {
    const user: Partial<UserType> = JSON.parse(value);
    dispatch({
      type: 'LOGIN',
      payload: { user, registered: user.patientSet!.edges.length > 0 },
    });
    return user;
  }
};

const initialState: StateType = {
  user: null,
  registered: false,
};

const AuthContext = React.createContext<AuthContextType>({
  user: null,
  registered: false,
  login: (tokenData: TokenAuthMutation) => {},
  register: (userData: UserRegisterMutation) => {},
  logout: () => {},
  setUser: (user: Partial<UserType>) => {},
  isFetchingFromStorage: true,
});

const authReducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case 'REGISTER': {
      const { user } = action.payload!;
      return {
        ...state,
        user,
      };
    }
    case 'LOGIN': {
      const { user, registered } = action.payload!;
      return {
        ...state,
        user,
        registered,
      };
    }
    case 'LOGOUT': {
      return initialState;
    }
    case 'SET_USER': {
      const { registered, user } = action.payload!;
      return {
        ...state,
        user,
        registered,
      };
    }
    default:
      return state;
  }
};

const AuthProvider = (props: any) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isFetchingFromStorage, setIsFetchingFromStorage] = useState(true);

  useEffect(() => {
    getInitialStateFromLocalStorage(dispatch).finally(() => {
      setIsFetchingFromStorage(false);
    });
  }, []);

  const login = async (tokenData: TokenAuthMutation) => {
    const { user, token, registered, refreshToken } = tokenData.tokenAuth;

    await Preferences.set({
      key: 'user',
      value: JSON.stringify(user),
    });

    await Preferences.set({
      key: 'token',
      value: token,
    });

    await Preferences.set({
      key: 'refreshToken',
      value: refreshToken,
    });

    dispatch({
      type: 'LOGIN',
      payload: { registered, user: user as UserType },
    });
  };

  const logout = async () => {
    await Preferences.remove({ key: 'user' });
    await Preferences.remove({ key: 'token' });
    await Preferences.remove({ key: 'refreshToken' });
    dispatch({
      type: 'LOGOUT',
    });
  };

  const register = (userData: UserRegisterMutation) => {
    const { user } = userData.userRegister;
    dispatch({
      type: 'REGISTER',
      payload: { user, registered: false },
    });
  };

  const setUser = async (user: Partial<UserType>) => {
    await Preferences.set({
      key: 'user',
      value: JSON.stringify(user),
    });

    dispatch({
      type: 'SET_USER',
      payload: { registered: user.patientSet!.edges.length > 0, user },
    });
  };

  const value = useMemo(
    () => ({
      user: state.user,
      registered: state.registered,
      login,
      logout,
      register,
      setUser,
      isFetchingFromStorage,
    }),
    [isFetchingFromStorage, state.registered, state.user]
  );

  return <AuthContext.Provider value={value} {...props} />;
};

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
